<script setup>
import { useMediaQuery } from '@vueuse/core';
const props = defineProps({
  data: {
    type: Object,
    default: () => {},
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
});
const isDesktop = useMediaQuery('(min-width: 1024px)');
const { getFormattedDates } = useDateTranslation();
const formattedDates = computed(() => {
  return getFormattedDates(props.data.duration);
});
</script>
<template>
  <div
    :class="[
      backgroundColor
        ? `promo-item promo-item--${backgroundColor}`
        : 'promo-item',
    ]"
  >
    <div class="promo-item__media">
      <NuxtImg
        v-if="data?.image?.src"
        class="promo-item__image"
        :src="data?.image?.src"
        :alt="data?.image?.alt"
        :height="data?.image?.height"
        :width="data?.image?.width"
        loading="lazy"
      />
    </div>
    <div class="promo-item__content">
      <p class="mb-0 promo-item__title h3" v-html="data?.title"></p>
      <div class="promo-item__bottom">
        <div
          class="promo-item__description body-3-regular"
          v-html="data?.description"
        ></div>
        <div
          v-if="formattedDates?.startDate || formattedDates?.endDate"
          class="promo-item__duration body-3-regular"
        >
          {{ $t('promotion.duration_start') }}
          <span
            v-if="formattedDates?.startDate || formattedDates?.endDate"
            class="promo-item__duration-time"
          >
            <template v-if="formattedDates.startDate">
              {{ $t('promotion.duration_from') }} {{ formattedDates.startDate }}
            </template>
            <template v-if="formattedDates.endDate">
              {{ $t('promotion.duration_between') }}
              {{ formattedDates.endDate }}
            </template>
          </span>
        </div>
        <Button v-if="isDesktop" v-slot="slotProps" as-child rounded>
          <CTagDetect
            :to="data?.url"
            target="_blank"
            :class="slotProps.class"
            class="promo-item__button button-black link-normal js-button-promo"
          >
            {{ $t('promotion.button') }}
          </CTagDetect>
        </Button>
      </div>
    </div>
    <Button v-if="!isDesktop" v-slot="slotProps" as-child rounded>
      <CTagDetect
        :to="data?.url"
        target="_blank"
        :class="slotProps.class"
        class="promo-item__button button-black link-normal js-button-promo"
      >
        {{ $t('promotion.button') }}
      </CTagDetect>
    </Button>
  </div>
</template>
<style scoped lang="scss">
@use 'sass:math';
.promo-item {
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px;
  grid-template-areas: 'media' 'content' 'button';
  @include media-breakpoint-up(sm) {
    gap: 40px;
    grid-template-columns: 4.85fr 7.15fr;
    grid-template-areas: 'media content' '. button';
    padding: 48px;
  }
  @include media-breakpoint-up(md) {
    grid-template-columns: 3.2fr 8.8fr;
    grid-template-areas: 'media content';
  }
  &--white {
    background-color: $gray;
  }
  &--gray {
    background-color: $white;
  }
  &--black {
    background-color: rgba($surface-container-opacity, 0.5);
  }
  &__media {
    grid-area: media;
  }
  &__image {
    aspect-ratio: 280/280;
    border-radius: 4px;
    height: auto;
    width: 100%;
  }
  &__content {
    grid-area: content;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-top: 10px;
    @include media-breakpoint-up(sm) {
      padding-top: 0px;
    }
  }
  &__bottom {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas: 'description' 'duration';
    gap: 16px;
    @include media-breakpoint-up(md) {
      grid-template-columns: 3.9fr 3fr auto;
      grid-template-areas: 'description duration button';
      margin-top: auto;
      align-items: end;
      gap: 40px;
    }
  }
  &__description {
    grid-area: description;
  }
  &__duration {
    grid-area: duration;
    &-time {
      display: block;
    }
    @include media-breakpoint-up(sm) {
      max-width: 75%;
    }
    @include media-breakpoint-up(md) {
      max-width: unset;
    }
  }
  &__button {
    grid-area: button;
    width: fit-content;
  }
}
</style>
